import React, { useState } from 'react';
import { LanguageCode, SiteSettings, MemberUser } from '../types';
import { translations } from '../i18n/translations';
import { parseStreamEmbedUrl } from '../utils/stream';
import { MessageSquare, Send, Users, Lock, ShieldCheck, UserCheck, LogIn } from 'lucide-react';

interface LiveStreamViewProps {
  currentLang: LanguageCode;
  settings: SiteSettings;
  isLoggedIn?: boolean;
  currentUser?: MemberUser | null;
  onOpenAuthModal?: () => void;
}

export const LiveStreamView: React.FC<LiveStreamViewProps> = ({
  currentLang,
  settings,
  isLoggedIn = false,
  currentUser = null,
  onOpenAuthModal,
}) => {
  const t = translations[currentLang];
  const [chatMessages, setChatMessages] = useState([
    { id: '1', user: 'Botan Amed', text: 'Bi xêr hatin weşanê!', time: '19:02' },
    { id: '2', user: 'Zerya Dersim', text: 'Muzîka Dengbêjiyê gelek xweş e, spas!', time: '19:04' },
    { id: '3', user: 'Cîhan Yılmaz', text: 'Fransa derneğimize sevgi ve selamlar.', time: '19:05' },
  ]);
  const [inputMsg, setInputMsg] = useState('');

  const handleSendChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMsg.trim()) return;
    const authorName = currentUser?.fullName || 'User';
    setChatMessages([
      ...chatMessages,
      {
        id: Date.now().toString(),
        user: authorName,
        text: inputMsg,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ]);
    setInputMsg('');
  };

  const getLiveTitle = () => {
    const titles: Record<LanguageCode, string> = {
      KM: 'Semînera Taybet a Hefteya Zimanê Kurdî & Şeva Dengbêjiyê (WEŞANA ZINDÎ)',
      FR: 'Séminaire Spécial Semaine de la Langue Kurde & Concert Dengbêj (EN DIRECT)',
      DE: 'Sonderseminar zur Kurdischen Sprachwoche & Dengbêj-Konzert (LIVE STREAM)',
      EN: 'Kurdish Language Week Special Seminar & Dengbêj Performance (LIVE STREAM)',
      TR: 'Kürt Dil Haftası Özel Semineri & Dengbêj Dinletisi (CANLI YAYIN)',
    };
    return titles[currentLang] || settings.liveStream?.title || '';
  };

  const getLiveSub = () => {
    const subs: Record<LanguageCode, string> = {
      KM: 'Semînerên derneka me ya zindî û weşanên çandî yên li ser YouTube, Facebook û Zoom.',
      FR: 'Séminaires et émissions culturelles en direct sur YouTube, Facebook et Zoom.',
      DE: 'Kultur- und Bildungssendungen live über YouTube, Facebook und Zoom.',
      EN: 'Our live seminars and cultural broadcasts on YouTube, Facebook, and Zoom.',
      TR: 'YouTube, Facebook ve Zoom üzerinden yayınlanan canlı dernek seminerlerimiz ve kültür yayınlarımız.',
    };
    return subs[currentLang] || '';
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-red-950/90 border border-red-500/60 text-red-400 text-xs font-extrabold uppercase tracking-wider shadow-[0_0_15px_rgba(239,68,68,0.4)]">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping" />
          <span>{t.liveNow}</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {getLiveTitle()}
        </h1>
        <p className="text-sm text-gray-300">
          {getLiveSub()}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Video Stream Player */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-[#132238] rounded-3xl overflow-hidden border-2 border-[#38BDF8]/40 shadow-[0_0_35px_rgba(56,189,248,0.2)] bg-black">
            <div className="relative aspect-video w-full">
              <iframe
                src={parseStreamEmbedUrl(settings.liveStream.embedUrl || settings.liveStream.youtubeUrl || '')}
                title="Live Stream Player"
                className="w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-4 sm:p-6 flex flex-wrap items-center justify-between gap-4 border-t border-[#38BDF8]/20 bg-[#0B1426]">
              <div className="flex items-center gap-3">
                <span className="px-2.5 py-1 rounded-md bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-widest shadow-md">
                  {t.liveNow}
                </span>
                <span className="text-xs text-gray-200 font-bold">
                  {settings.liveStream.platform}
                </span>
              </div>

              <div className="flex items-center gap-2 text-xs text-cyan-300 font-bold bg-[#132238] px-3.5 py-1.5 rounded-xl border border-[#38BDF8]/30 shadow-md">
                <Users className="w-4 h-4 text-[#38BDF8]" />
                <span>{settings.liveStream.viewerCount} {t.liveViewersCount}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Live Audience Chat */}
        <div className="bg-[#132238] rounded-3xl p-5 border-2 border-[#38BDF8]/30 flex flex-col justify-between min-h-[480px] lg:h-auto relative overflow-hidden shadow-xl">
          <div>
            <div className="flex items-center justify-between pb-3 border-b border-[#38BDF8]/20 mb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#38BDF8]" />
                <span>{t.liveChatTitle}</span>
              </h3>
              {isLoggedIn ? (
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-bold flex items-center gap-1">
                  <UserCheck className="w-3 h-3" />
                  <span>Member</span>
                </span>
              ) : (
                <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] font-bold flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  <span>Protected</span>
                </span>
              )}
            </div>

            {!isLoggedIn ? (
              <div className="flex flex-col items-center justify-center text-center p-6 space-y-4 my-8 rounded-2xl bg-[#0B1426] border border-[#38BDF8]/30 shadow-inner">
                <div className="w-14 h-14 rounded-full bg-[#132238] text-[#38BDF8] border-2 border-[#38BDF8] flex items-center justify-center shadow-[0_0_20px_rgba(56,189,248,0.4)] animate-pulse">
                  <Lock className="w-7 h-7" />
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-extrabold text-white">
                    🔒 {t.liveChatMembersOnly}
                  </h4>
                  <p className="text-xs text-gray-300 leading-relaxed max-w-xs">
                    {t.chatMembersOnlyDesc}
                  </p>
                </div>

                <button
                  onClick={onOpenAuthModal}
                  className="px-5 py-2.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_20px_rgba(165,243,252,0.8)] transition-all shadow-lg flex items-center gap-2 mt-2 cursor-pointer active:scale-95"
                >
                  <LogIn className="w-4 h-4" />
                  <span>{t.memberLogin}</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3 overflow-y-auto max-h-[340px] pr-1">
                <div className="p-2.5 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-[11px] text-emerald-200 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span>{t.chatMembersOnlyDesc}</span>
                </div>

                {chatMessages.map((msg) => (
                  <div key={msg.id} className="p-2.5 rounded-xl bg-[#0B1426] border border-[#38BDF8]/20 text-xs">
                    <div className="flex items-center justify-between text-[10px] text-[#38BDF8] font-bold mb-1">
                      <span>{msg.user}</span>
                      <span className="text-gray-400">{msg.time}</span>
                    </div>
                    <p className="text-gray-200">{msg.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {isLoggedIn && (
            <form onSubmit={handleSendChat} className="flex gap-2 pt-3 border-t border-[#38BDF8]/20">
              <input
                type="text"
                value={inputMsg}
                onChange={(e) => setInputMsg(e.target.value)}
                placeholder={t.typeYourMessage}
                className="flex-1 px-3 py-2 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
              />
              <button type="submit" className="p-2 rounded-xl bg-[#38BDF8] text-[#0B1426] font-extrabold hover:bg-[#A5F3FC]">
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
